/**
 * DebugLogger — centralized debug logging utility.
 *
 * Per-component enable/disable, format `[YYYY-MM-DD HH:mm:ss.SSS][Component][Method] message`,
 * safe no-op when disabled. Accessible via import and via window.DebugLogger.
 */

export type StackMode = 'trace' | 'error' | 'string'

class DebugLoggerClass {
  enabledComponents: Set<string> = new Set()
  disabledComponents: Set<string> = new Set()
  enableAllLogs = false
  disableAllLogs = false
  includeStack = false
  stackMode: StackMode = 'trace'

  static pad(num: number, length = 2): string {
    const str = String(num)
    return str.length >= length ? str : '0'.repeat(length - str.length) + str
  }

  static formatTimestamp(date: Date = new Date()): string {
    const yyyy = date.getFullYear()
    const MM = DebugLoggerClass.pad(date.getMonth() + 1)
    const dd = DebugLoggerClass.pad(date.getDate())
    const HH = DebugLoggerClass.pad(date.getHours())
    const mm = DebugLoggerClass.pad(date.getMinutes())
    const ss = DebugLoggerClass.pad(date.getSeconds())
    const SSS = DebugLoggerClass.pad(date.getMilliseconds(), 3)
    return `${yyyy}-${MM}-${dd} ${HH}:${mm}:${ss}.${SSS}`
  }

  enableAll(): this {
    this.enableAllLogs = true
    this.disableAllLogs = false
    return this
  }

  disableAll(): this {
    this.disableAllLogs = true
    this.enableAllLogs = false
    return this
  }

  reset(): this {
    this.enabledComponents.clear()
    this.disabledComponents.clear()
    this.enableAllLogs = false
    this.disableAllLogs = false
    this.includeStack = false
    this.stackMode = 'trace'
    return this
  }

  enable(componentName: string): this {
    if (typeof componentName === 'string' && componentName.trim().length > 0) {
      this.enabledComponents.add(componentName)
      this.disabledComponents.delete(componentName)
    }
    return this
  }

  disable(componentName: string): this {
    if (typeof componentName === 'string') {
      this.enabledComponents.delete(componentName)
      this.disabledComponents.add(componentName)
    }
    return this
  }

  setEnabledComponents(componentNames: string[] = []): this {
    this.enabledComponents = new Set(
      (Array.isArray(componentNames) ? componentNames : []).filter(Boolean)
    )
    return this
  }

  isEnabled(componentName: string): boolean {
    if (this.disableAllLogs) return false
    if (this.disabledComponents.has(componentName)) return false
    if (this.enableAllLogs) return true
    if (this.enabledComponents.size === 0) return true
    return this.enabledComponents.has(componentName)
  }

  static getCallerStack(): string {
    try {
      const err = new Error()
      if (!err.stack) return ''
      const lines = String(err.stack).split('\n')
      return lines.slice(2).join('\n')
    } catch {
      return ''
    }
  }

  enableStack(): this {
    this.includeStack = true
    return this
  }

  disableStack(): this {
    this.includeStack = false
    return this
  }

  setStackMode(mode: StackMode): this {
    if (mode === 'trace' || mode === 'error' || mode === 'string') {
      this.stackMode = mode
    }
    return this
  }

  private _emit(
    level: 'log' | 'info' | 'warn' | 'error',
    componentName: string,
    methodName: string,
    messageParts: unknown[]
  ): void {
    if (!this.isEnabled(componentName)) return
    const ts = DebugLoggerClass.formatTimestamp()
    const prefix = `[${ts}][${componentName}][${methodName}]`

    if (!this.includeStack) {
      console[level](prefix, ...messageParts)
      return
    }

    if (this.stackMode === 'error') {
      const text = messageParts.map((p) => (typeof p === 'string' ? p : JSON.stringify(p))).join(' ')
      console.error(new Error(`${prefix} ${text}`))
    } else if (this.stackMode === 'string') {
      const stack = DebugLoggerClass.getCallerStack()
      console[level](prefix, ...messageParts, `\nStack:\n${stack}`)
    } else {
      console[level](prefix, ...messageParts)
      console.trace()
    }
  }

  log(componentName: string, methodName: string, ...messageParts: unknown[]): void {
    this._emit('log', componentName, methodName, messageParts)
  }

  info(componentName: string, methodName: string, ...messageParts: unknown[]): void {
    this._emit('info', componentName, methodName, messageParts)
  }

  warn(componentName: string, methodName: string, ...messageParts: unknown[]): void {
    this._emit('warn', componentName, methodName, messageParts)
  }

  error(componentName: string, methodName: string, ...messageParts: unknown[]): void {
    this._emit('error', componentName, methodName, messageParts)
  }
}

const __debugLoggerSingleton = new DebugLoggerClass()

export const DebugLogger = __debugLoggerSingleton
export const log = (componentName: string, methodName: string, ...messageParts: unknown[]) =>
  __debugLoggerSingleton.log(componentName, methodName, ...messageParts)
export const info = (componentName: string, methodName: string, ...messageParts: unknown[]) =>
  __debugLoggerSingleton.info(componentName, methodName, ...messageParts)
export const warn = (componentName: string, methodName: string, ...messageParts: unknown[]) =>
  __debugLoggerSingleton.warn(componentName, methodName, ...messageParts)
export const error = (componentName: string, methodName: string, ...messageParts: unknown[]) =>
  __debugLoggerSingleton.error(componentName, methodName, ...messageParts)
export const enable = (componentName: string) => __debugLoggerSingleton.enable(componentName)
export const disable = (componentName: string) => __debugLoggerSingleton.disable(componentName)
export const enableAll = () => __debugLoggerSingleton.enableAll()
export const disableAll = () => __debugLoggerSingleton.disableAll()
export const reset = () => __debugLoggerSingleton.reset()
export const setEnabledComponents = (names: string[]) => __debugLoggerSingleton.setEnabledComponents(names)
export const isEnabled = (name: string) => __debugLoggerSingleton.isEnabled(name)

// Active debug toggles (uncomment to enable)
__debugLoggerSingleton.disableAll();
// __debugLoggerSingleton.enable('DataTable')
// __debugLoggerSingleton.enable('TranslationsTable')
// __debugLoggerSingleton.enable('useTranslationsAdmin')

export interface StatusTraceData {
  status?: number | string
  periodId?: number | string
  period_id?: number | string
  [key: string]: unknown
}

export const statusTrace = (location: string, action: string, data: StatusTraceData = {}): void => {
  const ts = DebugLoggerClass.formatTimestamp()
  const statusLabels: Record<string, string> = {
    '-1': 'ARCHIVED',
    '0': 'OPEN',
    '1': 'REVENUE',
    '2': 'EXPENSES',
    '3': 'CALCULATED',
    '4': 'CONFIRMED',
    '5': 'PAID'
  }
  const statusLabel =
    data.status !== undefined ? statusLabels[String(data.status)] || `UNKNOWN(${data.status})` : 'N/A'
  const periodId = data.periodId || data.period_id || 'N/A'

  console.warn(
    `🔍 STATUS_TRACE [${ts}] [${location}] [${action}] periodId=${periodId} status=${data.status}(${statusLabel})`,
    data
  )
}
